import { redirect } from 'react-router-dom'

import type { AuthUser } from '@/providers/auth-provider'

import { api } from '@/lib/api-client'
import { hasAnyPermission, type Permission } from '@/lib/permissions'
import { queryClient } from '@/lib/query-client'

async function fetchSession(): Promise<AuthUser | null> {
  try {
    const { data } = await api.get<{
      user?: AuthUser
      permissions?: readonly string[]
    } | null>('/api/auth/get-session')
    if (!data?.user) return null
    return { ...data.user, permissions: data.permissions ?? [] }
  } catch {
    return null
  }
}

async function ensureSession(): Promise<AuthUser | null> {
  const cached = queryClient.getQueryData<AuthUser | null>(['auth', 'session'])
  if (cached !== undefined) return cached

  const user = await queryClient.fetchQuery({
    queryKey: ['auth', 'session'],
    queryFn: fetchSession,
    staleTime: 60_000,
    retry: false,
  })

  return user
}

function buildLoginRedirect(request: Request): Response {
  const url = new URL(request.url)
  const next = encodeURIComponent(url.pathname + url.search)
  return redirect(`/login?next=${next}`)
}

export async function adminAuthLoader({ request }: { request: Request }): Promise<AuthUser> {
  const user = await ensureSession()
  if (!user) {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw buildLoginRedirect(request)
  }
  return user
}

export function createPermissionLoader(required: Permission[]) {
  return async ({ request }: { request: Request }): Promise<AuthUser> => {
    const user = await ensureSession()
    if (!user) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw buildLoginRedirect(request)
    }
    if (!hasAnyPermission(user.permissions, required)) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect('/admin')
    }
    return user
  }
}

export async function logoutAdmin(): Promise<void> {
  try {
    await api.post('/api/auth/sign-out')
  } catch {
    // Silenciamos pra garantir que UI sai mesmo se a requisição falhar.
  }
  await queryClient.resetQueries({ queryKey: ['auth', 'session'] })
}
